/**
 * Serviço de API para splits (globais e por campanha). Admin only.
 */

import { api } from '@/lib/api/client'
import type {
  GlobalSplitsListResponse,
  SplitCreatePayload,
  SplitUpdatePayload,
} from '@/types/api'

/** Resposta de listagem de splits de campanha (mesmo formato que global: data.splits) */
interface CampaignSplitsListResponse {
  splits: import('@/types/api').Split[]
}

export async function fetchGlobalSplits(): Promise<GlobalSplitsListResponse> {
  return api.get<GlobalSplitsListResponse>('/api/admin/splits/global', {
    requireAuth: true,
  })
}

const AVATAR_MASCOT_SPLITS_PATH = '/api/admin/splits/avatar-mascot'

const AVATAR_ADMIN_SPLITS_PATH = '/api/admin/splits/avatar-admin'

export function fetchAvatarAdminSplits(): Promise<GlobalSplitsListResponse> {
  return api.get(AVATAR_ADMIN_SPLITS_PATH, { requireAuth: true })
}

export function createAvatarAdminSplit(
  payload: SplitCreatePayload
): Promise<{ split: import('@/types/api').Split }> {
  return api.post(AVATAR_ADMIN_SPLITS_PATH, payload, { requireAuth: true })
}

export function updateAvatarAdminSplit(
  id: number,
  payload: SplitUpdatePayload
): Promise<{ split: import('@/types/api').Split }> {
  return api.put(`${AVATAR_ADMIN_SPLITS_PATH}/${id}`, payload, { requireAuth: true })
}

export async function deleteAvatarAdminSplit(id: number): Promise<void> {
  await api.delete(`${AVATAR_ADMIN_SPLITS_PATH}/${id}`, { requireAuth: true })
}

export async function fetchAvatarMascotSplits(): Promise<GlobalSplitsListResponse> {
  return api.get<GlobalSplitsListResponse>(AVATAR_MASCOT_SPLITS_PATH, {
    requireAuth: true,
  })
}

export async function createAvatarMascotSplit(
  payload: SplitCreatePayload
): Promise<{ split: import('@/types/api').Split }> {
  return api.post(AVATAR_MASCOT_SPLITS_PATH, payload, { requireAuth: true })
}

export async function updateAvatarMascotSplit(
  id: number,
  payload: SplitUpdatePayload
): Promise<{ split: import('@/types/api').Split }> {
  return api.put(`${AVATAR_MASCOT_SPLITS_PATH}/${id}`, payload, { requireAuth: true })
}

export async function deleteAvatarMascotSplit(id: number): Promise<void> {
  await api.delete(`${AVATAR_MASCOT_SPLITS_PATH}/${id}`, { requireAuth: true })
}

export async function createGlobalSplit(
  payload: SplitCreatePayload
): Promise<{ split: import('@/types/api').Split }> {
  return api.post<{ split: import('@/types/api').Split }>(
    '/api/admin/splits/global',
    payload,
    { requireAuth: true }
  )
}

export async function updateGlobalSplit(
  id: number,
  payload: SplitUpdatePayload
): Promise<{ split: import('@/types/api').Split }> {
  return api.put<{ split: import('@/types/api').Split }>(
    `/api/admin/splits/global/${id}`,
    payload,
    { requireAuth: true }
  )
}

/** DELETE retorna data: [] no backend para compatibilidade com o client. */
export async function deleteGlobalSplit(id: number): Promise<void> {
  await api.delete<unknown>(`/api/admin/splits/global/${id}`, {
    requireAuth: true,
  })
}

export async function fetchCampaignSplits(
  campaignId: number
): Promise<CampaignSplitsListResponse> {
  return api.get<CampaignSplitsListResponse>(
    `/api/admin/campaigns/${campaignId}/splits`,
    { requireAuth: true }
  )
}

export async function createCampaignSplit(
  campaignId: number,
  payload: SplitCreatePayload
): Promise<{ split: import('@/types/api').Split }> {
  return api.post<{ split: import('@/types/api').Split }>(
    `/api/admin/campaigns/${campaignId}/splits`,
    payload,
    { requireAuth: true }
  )
}

export async function updateCampaignSplit(
  campaignId: number,
  id: number,
  payload: SplitUpdatePayload
): Promise<{ split: import('@/types/api').Split }> {
  return api.put<{ split: import('@/types/api').Split }>(
    `/api/admin/campaigns/${campaignId}/splits/${id}`,
    payload,
    { requireAuth: true }
  )
}

export async function deleteCampaignSplit(campaignId: number, id: number): Promise<void> {
  await api.delete<unknown>(`/api/admin/campaigns/${campaignId}/splits/${id}`, {
    requireAuth: true,
  })
}
